import { fork, take, put, call, takeEvery } from 'redux-saga/effects';
import { initialize } from 'redux-form';

import {
  SETTING_RECEIPT_CHANGE_OUTLET,
  SETTING_RECEIPT_PAGE_REQUEST,
  SETTING_RECEIPT_SUBMIT_FORM,
  SETTING_RECEIPT_CHANGE_OUTLET_NAME,
} from '../../actions/setting/receipt/constants';
import * as action from '../../actions/index';
import * as actOutlet from '../../actions/outlet';
import * as actNotif from '../../actions/notifications';
import * as actReceipt from '../../actions/setting/receipt';

import * as apiOutlet from '../../api/outlets';
import * as apiProfile from '../../api/profile';
import { phoneNumberFormatter } from '../../utils/formatter';
import {
  getSelectedOutletIdFromLocalStorage,
  setSelectedOutletIdToLocalStorage,
} from '../../utils/selectedOutletId';
import { assertErrorHasMessage } from '../../utils/assert';
import * as clevertapSettingsReceipt from '../../clevertap/settings/receipt';

export function* fillForm(currentOutlet) {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { name, address, city, province, postal_code, notes, phone_number } =
    currentOutlet;
  yield put(
    initialize(
      'checkout',
      {
        name,
        address,
        city,
        province,
        zipcode: postal_code,
        phone: phoneNumberFormatter(phone_number),
        notes,
      },
      false
    )
  );
}

export function* initForm(currentBusiness, currentOutlet) {
  yield put(
    initialize(
      'checkout',
      {
        logo: currentOutlet.logo,
        website: currentOutlet.website || currentBusiness.website,
        twitter: currentOutlet.twitter || currentBusiness.twitter,
        facebook: currentOutlet.facebook || currentBusiness.facebook,
        instagram: currentOutlet.instagram || currentBusiness.instagram,
        name: currentOutlet.name,
        address: currentOutlet.address,
        city: currentOutlet.city,
        province: currentOutlet.province,
        zipcode: currentOutlet.postal_code,
        phone:
          currentOutlet.phone_number &&
          phoneNumberFormatter(currentOutlet.phone_number),
        notes: currentOutlet.notes,
      },
      false
    )
  );
}

export function* fillSearchForm(outletIds) {
  yield put(
    initialize('search', {
      outlet: outletIds,
    })
  );
}

export function* pageRequestFlow() {
  try {
    yield put(action.sendingRequest(true));
    yield put(action.showProgressBar());

    let outletRes: any = null;

    const res = yield call(apiOutlet.getOutlets, { size: 300 });
    outletRes = res && res.data;

    const outlets = outletRes.data;
    yield put(actReceipt.setOutlets(outlets));

    const currentProfileRes = yield call(apiProfile.getProfile);
    const currentProfile = currentProfileRes.data;
    yield put(actReceipt.setProfile(currentProfile));

    const outletIds = getSelectedOutletIdFromLocalStorage();
    const currentOutlet = yield call(apiOutlet.outlets, {
      ids: outletIds[0],
    });
    yield put(actOutlet.setCurrentOutlet(currentOutlet));
    const selectedOutlet = currentOutlet.data;

    const logoOutlet = yield call(
      apiOutlet.getOutletBusinessService,
      outletIds[0],
      {}
    );
    const { data } = logoOutlet && logoOutlet.data;
    yield call(initForm, currentProfile.business, data);

    // only assign first outlet, if > 0 because it means select all outlet
    yield call(fillSearchForm, [outletIds[0]]);
    clevertapSettingsReceipt.settingReceiptStart(selectedOutlet);
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(action.sendingRequest(false));
    yield put(action.hideProgressBar());
  }
}

export function* changeOutletFlow() {
  // noinspection InfiniteLoopJS
  while (true) {
    const {
      payload: { outletIds },
    } = yield take(SETTING_RECEIPT_CHANGE_OUTLET);
    try {
      yield put(action.sendingRequest(true));
      yield put(action.showProgressBar());
      const currentProfileRes = yield call(apiProfile.getProfile);
      const currentProfile = currentProfileRes.data;
      yield put(actReceipt.setProfile(currentProfile));

      setSelectedOutletIdToLocalStorage(outletIds);
      const currentOutlet = yield call(apiOutlet.outlets, {
        ids: outletIds[0],
      });
      yield put(actOutlet.setCurrentOutlet(currentOutlet));

      const chosenOutlet = yield call(
        apiOutlet.getOutletBusinessService,
        outletIds,
        {}
      );
      const { data } = chosenOutlet && chosenOutlet.data;
      yield call(initForm, currentProfile.business, data);

      yield call(fillSearchForm, outletIds);

      let outletsRes: any = null;

      const res = yield call(apiOutlet.getOutlets);
      outletsRes = res && res.data;

      const outlets = outletsRes.data;
      yield put(actOutlet.setOutlets(outlets));
    } finally {
      yield put(action.sendingRequest(false));
      yield put(action.hideProgressBar());
    }
  }
}

export function* changeOutletNameFlow() {
  // noinspection InfiniteLoopJS
  while (true) {
    const {
      payload: { outlets, currentProfile, name },
    } = yield take(SETTING_RECEIPT_CHANGE_OUTLET_NAME);
    let selectedOutlet;
    const localStorageSelectedOutlet =
      getSelectedOutletIdFromLocalStorage()?.[0];
    outlets.forEach((outlet) => {
      if (outlet.id === localStorageSelectedOutlet) {
        outlet.name = name;
        selectedOutlet = outlet;
      }
    });
    yield put(actOutlet.setCurrentOutlet({ data: [selectedOutlet] }));
    yield put(actOutlet.setOutlets(outlets));
    yield put(actReceipt.setProfile(currentProfile));
  }
}

export function* submitFormFlow() {
  // noinspection InfiniteLoopJS
  while (true) {
    const {
      payload: { data, outletId },
    } = yield take(SETTING_RECEIPT_SUBMIT_FORM);
    try {
      yield put(action.sendingRequest(true));
      yield put(action.showProgressBar());

      yield call(apiOutlet.updateOutlet, outletId, data, {});

      yield put(actNotif.success('Outlet was successfully updated.'));

      let outletRes: any = null;

      const res = yield call(apiOutlet.getOutlets);
      outletRes = res && res.data;

      const outlets = outletRes.data;
      yield put(actReceipt.setOutlets(outlets));

      // call get profile again and then reinitialize the form
      const currentProfileRes = yield call(apiProfile.getProfile);
      const currentProfile = currentProfileRes.data;
      yield put(actReceipt.setProfile(currentProfile));
      const currentOutlet = yield call(apiOutlet.outlets, { ids: outletId });
      yield put(actOutlet.setCurrentOutlet(currentOutlet));

      const newSelectedOutlet = yield call(
        apiOutlet.getOutletBusinessService,
        outletId,
        {}
      );
      const { data: outlet } = newSelectedOutlet && newSelectedOutlet.data;
      yield call(initForm, currentProfile.business, outlet);

      // only assign first outlet, if > 0 because it means select all outlet
      yield call(fillSearchForm, [outletId]);
    } catch (error) {
      assertErrorHasMessage(error);
      yield put(actNotif.error(error.message));
    } finally {
      yield put(action.sendingRequest(false));
      yield put(action.hideProgressBar());
    }
  }
}

export default function* settingReceiptFlowSaga() {
  yield fork(changeOutletFlow);
  yield takeEvery(SETTING_RECEIPT_PAGE_REQUEST, pageRequestFlow);
  yield fork(submitFormFlow);
  yield fork(changeOutletNameFlow);
}
